import React from "react";
import { createPortal } from "react-dom";
import { getReactPortalRoot } from "../utils/reactPortalRoot.js";
import FloatingRouteMapSurface from "./FloatingRouteMapSurface.jsx";
import RouteMapPanel from "./RouteMapPanel.jsx";
import RouteMapPictureInPicturePanel from "./RouteMapPictureInPicturePanel.jsx";
import { useToast } from "./ResultsPrimitives.jsx";
import { FLOATING_MAP_INITIAL_POSITION, FLOATING_MAP_PANEL_INSET, FLOATING_MAP_SIZE_PRESETS, clampFloatingMapDocumentPosition, clampFloatingMapHeight, clampFloatingMapWidth, resolveAbsoluteFloatingMapAnchorPosition, resolveDefaultFloatingMapDocumentPosition, resolveFloatingMapDimensions } from "../utils/floatingRouteMapLayout.js";
import {
  closeRouteMapPictureInPictureWindow,
  openRouteMapPictureInPictureWindow,
  resolveRouteMapPictureInPictureDefaultSize,
  resolveRouteMapPictureInPictureScreenSize,
} from "../utils/routeMapPictureInPictureWindow.js";
import {
  clearRouteMapPictureInPictureRestoreState,
  persistRouteMapPictureInPictureRestoreState,
  readRouteMapPictureInPictureRestoreState,
} from "../utils/routeMapPictureInPictureRestoreState.js";

function resolveRouteMapTitle(assignment, trip) {
  const assignmentName = typeof assignment?.truck_name === "string" ? assignment.truck_name.trim() : "";
  if (assignmentName) {
    return assignmentName;
  }

  const assignmentId = typeof assignment?.truck_id === "string" ? assignment.truck_id.trim() : "";
  if (assignmentId) {
    return assignmentId;
  }

  const tripId = typeof trip?.trip_id === "string" ? trip.trip_id.trim() : "";
  return tripId || "Lecture courante";
}

function resolveRouteMapSubtitle(trip) {
  const tripId = typeof trip?.trip_id === "string" ? trip.trip_id.trim() : "";
  return tripId ? `Synchronisée sur ${tripId}` : "Synchronisée sur la sélection courante.";
}

export default function RouteFloatingMapPanel({
  anchorElement = null,
  assignment = null,
  isRouteViewActive = true,
  isMapFloating: controlledIsMapFloating = undefined,
  mountNode = null,
  observabilityItems = [],
  onClientSelect = null,
  onControllerReady = null,
  onMapFloatingChange = null,
  onMapRenderMetaChange = null,
  onRouteHighlightChange = null,
  selectedClientId = null,
  selectionRequest = null,
  showInlineHeader = false,
  stateRef,
  trip = null,
}) {
  const { showToast } = useToast();
  const floatingMapAnchorRef = React.useRef(null);
  const floatingMapAnchorPositionRef = React.useRef(null);
  const floatingMapDragRef = React.useRef(null);
  const floatingMapPanelRef = React.useRef(null);
  const floatingMapResizeRef = React.useRef(null);
  const hasCustomFloatingMapPositionRef = React.useRef(false);
  const hasShownPictureInPictureRestorePromptRef = React.useRef(false);
  const isPageUnloadingRef = React.useRef(false);

  const [internalIsMapFloating, setInternalIsMapFloating] = React.useState(false);
  const [routeMapPictureInPictureMountNode, setRouteMapPictureInPictureMountNode] = React.useState(null);
  const [isRouteMapPictureInPictureLinked, setIsRouteMapPictureInPictureLinked] = React.useState(false);
  const [isRouteMapPictureInPictureExpanded, setIsRouteMapPictureInPictureExpanded] = React.useState(false);
  const [floatingMapPosition, setFloatingMapPosition] = React.useState(FLOATING_MAP_INITIAL_POSITION);
  const [floatingMapSize, setFloatingMapSize] = React.useState("compact");
  const [isFloatingMapDragging, setIsFloatingMapDragging] = React.useState(false);
  const [isFloatingMapResizing, setIsFloatingMapResizing] = React.useState(false);
  const [floatingMapWidth, setFloatingMapWidth] = React.useState(null);
  const [floatingMapHeight, setFloatingMapHeight] = React.useState(null);

  const isMapFloating = typeof controlledIsMapFloating === "boolean"
    ? controlledIsMapFloating
    : internalIsMapFloating;

  const floatingMapMountNode = getReactPortalRoot();
  const setIsMapFloating = React.useCallback((nextValue) => {
    if (typeof onMapFloatingChange === "function") {
      onMapFloatingChange(nextValue);
      return;
    }

    setInternalIsMapFloating(nextValue);
  }, [onMapFloatingChange]);

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const markPageUnloading = () => {
      isPageUnloadingRef.current = true;
    };
    const clearPageUnloading = () => {
      isPageUnloadingRef.current = false;
    };

    window.addEventListener("beforeunload", markPageUnloading, true);
    window.addEventListener("pagehide", markPageUnloading, true);
    window.addEventListener("pageshow", clearPageUnloading, true);
    return () => {
      window.removeEventListener("beforeunload", markPageUnloading, true);
      window.removeEventListener("pagehide", markPageUnloading, true);
      window.removeEventListener("pageshow", clearPageUnloading, true);
    };
  }, []);

  const resetFloatingMapLayout = React.useCallback((nextSize = "compact") => {
    setFloatingMapPosition(FLOATING_MAP_INITIAL_POSITION);
    setFloatingMapSize(nextSize);
    setFloatingMapWidth(null);
    setFloatingMapHeight(null);
    setIsFloatingMapDragging(false);
    setIsFloatingMapResizing(false);
    floatingMapDragRef.current = null;
    floatingMapResizeRef.current = null;
    floatingMapAnchorPositionRef.current = null;
    hasCustomFloatingMapPositionRef.current = false;
  }, []);

  React.useEffect(() => {
    if (isRouteViewActive) {
      return;
    }

    setIsMapFloating(false);
    clearRouteMapPictureInPictureRestoreState();
    closeRouteMapPictureInPictureWindow();
    setRouteMapPictureInPictureMountNode(null);
    setIsRouteMapPictureInPictureLinked(false);
    setIsRouteMapPictureInPictureExpanded(false);
    resetFloatingMapLayout();
  }, [isRouteViewActive, resetFloatingMapLayout, setIsMapFloating]);

  React.useEffect(() => {
    if (isMapFloating) {
      return;
    }

    resetFloatingMapLayout();
  }, [isMapFloating, resetFloatingMapLayout]);

  React.useEffect(() => {
    if (!isMapFloating) {
      return;
    }

    floatingMapAnchorPositionRef.current = resolveAbsoluteFloatingMapAnchorPosition(anchorElement || floatingMapAnchorRef.current);
  }, [anchorElement, isMapFloating]);

  React.useEffect(() => {
    if (!isMapFloating) {
      return;
    }

    const panelElement = floatingMapPanelRef.current;
    if (!(panelElement instanceof HTMLElement)) {
      return;
    }

    setFloatingMapPosition((currentPosition) => (
      hasCustomFloatingMapPositionRef.current
        ? clampFloatingMapDocumentPosition(currentPosition, panelElement)
        : resolveDefaultFloatingMapDocumentPosition(panelElement, floatingMapAnchorPositionRef.current)
    ));

    panelElement.focus();
  }, [floatingMapHeight, floatingMapSize, floatingMapWidth, isMapFloating]);

  const handleFloatingMapPointerDown = React.useCallback((event) => {
    if (!isMapFloating || event.button !== 0) {
      return;
    }

    const panelElement = floatingMapPanelRef.current;
    if (!(panelElement instanceof HTMLElement)) {
      return;
    }

    event.preventDefault();
    try {
      panelElement.setPointerCapture?.(event.pointerId);
    } catch {
      // Ignore environments that do not expose an active pointer for capture.
    }
    floatingMapDragRef.current = {
      originX: floatingMapPosition.x,
      originY: floatingMapPosition.y,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
    };
    setIsFloatingMapDragging(true);
  }, [floatingMapPosition, isMapFloating]);

  const handleFloatingMapPointerMove = React.useCallback((event) => {
    const dragState = floatingMapDragRef.current;
    const panelElement = floatingMapPanelRef.current;
    if (!(panelElement instanceof HTMLElement)) {
      return;
    }

    if (dragState && event.pointerId === dragState.pointerId) {
      hasCustomFloatingMapPositionRef.current = true;
      setFloatingMapPosition(clampFloatingMapDocumentPosition(
        {
          x: dragState.originX + (event.clientX - dragState.startX),
          y: dragState.originY + (event.clientY - dragState.startY),
        },
        panelElement,
      ));
      return;
    }

    const resizeState = floatingMapResizeRef.current;
    if (!resizeState || event.pointerId !== resizeState.pointerId) {
      return;
    }

    const horizontalDelta = event.clientX - resizeState.startX;
    const verticalDelta = event.clientY - resizeState.startY;
    let nextWidth = resizeState.originWidth;
    let nextHeight = resizeState.originHeight;
    let nextX = resizeState.originX;
    let nextY = resizeState.originY;

    if (resizeState.direction.includes("east")) {
      nextWidth = clampFloatingMapWidth(resizeState.originWidth + horizontalDelta);
    }

    if (resizeState.direction.includes("west")) {
      nextWidth = clampFloatingMapWidth(resizeState.originWidth - horizontalDelta);
      nextX = resizeState.originX + (resizeState.originWidth - nextWidth);
      if (nextX < FLOATING_MAP_PANEL_INSET) {
        const overflow = FLOATING_MAP_PANEL_INSET - nextX;
        nextX = FLOATING_MAP_PANEL_INSET;
        nextWidth = clampFloatingMapWidth(nextWidth - overflow);
      }
    }

    if (resizeState.direction.includes("south")) {
      nextHeight = clampFloatingMapHeight(resizeState.originHeight + verticalDelta);
    }

    if (resizeState.direction.includes("north")) {
      nextHeight = clampFloatingMapHeight(resizeState.originHeight - verticalDelta);
      nextY = resizeState.originY + (resizeState.originHeight - nextHeight);
      if (nextY < FLOATING_MAP_PANEL_INSET) {
        const overflow = FLOATING_MAP_PANEL_INSET - nextY;
        nextY = FLOATING_MAP_PANEL_INSET;
        nextHeight = clampFloatingMapHeight(nextHeight - overflow);
      }
    }

    hasCustomFloatingMapPositionRef.current = true;
    setFloatingMapWidth(nextWidth);
    setFloatingMapHeight(nextHeight);
    setFloatingMapPosition((currentPosition) => (
      currentPosition.x === nextX && currentPosition.y === nextY
        ? currentPosition
        : { x: nextX, y: nextY }
    ));
  }, []);

  const handleFloatingMapPointerEnd = React.useCallback((event) => {
    const dragState = floatingMapDragRef.current;
    if (dragState && event.pointerId === dragState.pointerId) {
      if (event.currentTarget instanceof HTMLElement && event.currentTarget.hasPointerCapture?.(event.pointerId)) {
        try {
          event.currentTarget.releasePointerCapture(event.pointerId);
        } catch {
          // Ignore environments that already released the pointer.
        }
      }

      floatingMapDragRef.current = null;
      setIsFloatingMapDragging(false);
      return;
    }

    const resizeState = floatingMapResizeRef.current;
    if (!resizeState || event.pointerId !== resizeState.pointerId) {
      return;
    }

    if (event.currentTarget instanceof HTMLElement && event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch {
        // Ignore environments that already released the pointer.
      }
    }

    floatingMapResizeRef.current = null;
    setIsFloatingMapResizing(false);
  }, []);

  const handleFloatingMapKeyDown = React.useCallback((event) => {
    if (event.key !== "Escape") {
      return;
    }

    event.preventDefault();
    setIsMapFloating(false);
  }, []);

  const handleFloatingMapSizeChange = React.useCallback((nextSize) => {
    if (!FLOATING_MAP_SIZE_PRESETS[nextSize]) {
      return;
    }

    setFloatingMapSize(nextSize);
    setFloatingMapWidth(null);
    setFloatingMapHeight(null);
  }, []);

  const handleFloatingMapResizePointerDown = React.useCallback((event, direction = "south-east") => {
    if (!isMapFloating || event.button !== 0) {
      return;
    }

    const panelElement = floatingMapPanelRef.current;
    if (!(panelElement instanceof HTMLElement)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    try {
      panelElement.setPointerCapture?.(event.pointerId);
    } catch {
      // Ignore environments that do not expose an active pointer for capture.
    }

    const mapShellElement = panelElement.querySelector(".route-stage__map-shell");
    const originDimensions = resolveFloatingMapDimensions(floatingMapSize, floatingMapWidth, floatingMapHeight);

    floatingMapResizeRef.current = {
      direction,
      originHeight: mapShellElement instanceof HTMLElement
        ? mapShellElement.getBoundingClientRect().height
        : originDimensions.mapHeight,
      originWidth: originDimensions.width,
      originX: floatingMapPosition.x,
      originY: floatingMapPosition.y,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
    };
    setIsFloatingMapResizing(true);
  }, [floatingMapHeight, floatingMapPosition.x, floatingMapPosition.y, floatingMapSize, floatingMapWidth, isMapFloating]);

  React.useEffect(() => {
    if ((!isFloatingMapDragging && !isFloatingMapResizing) || typeof window === "undefined") {
      return undefined;
    }

    const handleWindowPointerMove = (event) => {
      handleFloatingMapPointerMove(event);
    };
    const handleWindowPointerEnd = (event) => {
      handleFloatingMapPointerEnd(event);
    };

    window.addEventListener("pointermove", handleWindowPointerMove, true);
    window.addEventListener("pointerup", handleWindowPointerEnd, true);
    window.addEventListener("pointercancel", handleWindowPointerEnd, true);

    return () => {
      window.removeEventListener("pointermove", handleWindowPointerMove, true);
      window.removeEventListener("pointerup", handleWindowPointerEnd, true);
      window.removeEventListener("pointercancel", handleWindowPointerEnd, true);
    };
  }, [handleFloatingMapPointerEnd, handleFloatingMapPointerMove, isFloatingMapDragging, isFloatingMapResizing]);

  const hasInlineMountNode = mountNode instanceof HTMLElement;
  const floatingMapDimensions = resolveFloatingMapDimensions(floatingMapSize, floatingMapWidth, floatingMapHeight);
  const floatingMapPanelStyle = isMapFloating
    ? {
        left: `${floatingMapPosition.x}px`,
        top: `${floatingMapPosition.y}px`,
        width: `${floatingMapDimensions.width}px`,
      }
    : undefined;
  const routeMapTitle = resolveRouteMapTitle(assignment, trip);
  const routeMapSubtitle = resolveRouteMapSubtitle(trip);
  const routeMapPictureInPictureTitle = routeMapTitle ? `Carte route - ${routeMapTitle}` : "Carte route";
  const persistRouteMapPictureInPictureRestore = React.useCallback(() => {
    persistRouteMapPictureInPictureRestoreState({
      assignmentId: assignment?.truck_id || "",
      scope: "route",
      title: routeMapPictureInPictureTitle,
      tripId: trip?.trip_id || "",
    });
  }, [assignment?.truck_id, routeMapPictureInPictureTitle, trip?.trip_id]);
  const closeRouteMapPictureInPicture = React.useCallback(() => {
    clearRouteMapPictureInPictureRestoreState();
    closeRouteMapPictureInPictureWindow();
    setRouteMapPictureInPictureMountNode(null);
    setIsRouteMapPictureInPictureLinked(false);
    setIsRouteMapPictureInPictureExpanded(false);
  }, []);
  const openFloatingMapOnSecondScreen = React.useCallback(async ({ restore = false } = {}) => {
    try {
      const pictureInPictureSize = resolveRouteMapPictureInPictureDefaultSize();
      const result = await openRouteMapPictureInPictureWindow({
        height: pictureInPictureSize.height,
        onClose: () => {
          setRouteMapPictureInPictureMountNode(null);
          setIsRouteMapPictureInPictureLinked(false);
          setIsRouteMapPictureInPictureExpanded(false);
          if (!isPageUnloadingRef.current) {
            clearRouteMapPictureInPictureRestoreState();
          }
        },
        title: routeMapPictureInPictureTitle,
        width: pictureInPictureSize.width,
      });

      if (result.ok) {
        setRouteMapPictureInPictureMountNode(result.mountNode || null);
        setIsRouteMapPictureInPictureLinked(true);
        setIsRouteMapPictureInPictureExpanded(false);
        persistRouteMapPictureInPictureRestore();
        setIsMapFloating(false);
        return;
      }

      showToast({
        type: "info",
        title: "Fenêtre détachée indisponible",
        message: result.reason === "detached-window-unsupported"
          ? "Le navigateur ne permet pas d'ouvrir la carte dans une fenêtre détachée."
          : "Le navigateur a bloqué l'ouverture de la fenêtre détachée. Autorise les fenêtres pop-up puis réessaie.",
        duration: 7000,
      });
      if (restore) {
        clearRouteMapPictureInPictureRestoreState();
      }
    } catch (error) {
      if (restore) {
        clearRouteMapPictureInPictureRestoreState();
      }
      showToast({
        type: "error",
        title: "Ouverture impossible",
        message: error instanceof Error && error.message
          ? error.message
          : "La fenêtre détachée n'a pas pu être créée.",
        duration: 7000,
      });
    }
  }, [persistRouteMapPictureInPictureRestore, routeMapPictureInPictureTitle, setIsMapFloating, showToast]);

  const handleRouteMapPictureInPictureExpandedChange = React.useCallback(async (nextExpanded, options = {}) => {
    if (options?.resizedInPlace === true) {
      setIsRouteMapPictureInPictureExpanded(nextExpanded);
      persistRouteMapPictureInPictureRestore();
      setIsMapFloating(false);
      return;
    }

    if (options?.unavailable === true) {
      showToast({
        type: "info",
        title: "Plein écran indisponible",
        message: "Le navigateur n'a pas permis de passer la fenêtre détachée en plein écran. La fenêtre reste ouverte au même endroit.",
        duration: 7000,
      });
      return;
    }

    try {
      const pictureInPictureSize = nextExpanded
        ? resolveRouteMapPictureInPictureScreenSize()
        : resolveRouteMapPictureInPictureDefaultSize();
      const result = await openRouteMapPictureInPictureWindow({
        height: pictureInPictureSize.height,
        onClose: () => {
          setRouteMapPictureInPictureMountNode(null);
          setIsRouteMapPictureInPictureLinked(false);
          setIsRouteMapPictureInPictureExpanded(false);
          if (!isPageUnloadingRef.current) {
            clearRouteMapPictureInPictureRestoreState();
          }
        },
        preferInitialWindowPlacement: true,
        replaceExisting: true,
        title: routeMapPictureInPictureTitle,
        width: pictureInPictureSize.width,
      });

      if (result.ok) {
        setRouteMapPictureInPictureMountNode(result.mountNode || null);
        setIsRouteMapPictureInPictureLinked(true);
        setIsRouteMapPictureInPictureExpanded(nextExpanded);
        persistRouteMapPictureInPictureRestore();
        setIsMapFloating(false);
        return;
      }

      showToast({
        type: "info",
        title: "Redimensionnement indisponible",
        message: "Le navigateur n'a pas permis d'agrandir la fenêtre détachée. Tu peux quand même l'agrandir manuellement depuis ses coins.",
        duration: 7000,
      });
    } catch (error) {
      showToast({
        type: "error",
        title: "Redimensionnement impossible",
        message: error instanceof Error && error.message
          ? error.message
          : "La fenêtre détachée n'a pas pu être redimensionnée.",
        duration: 7000,
      });
    }
  }, [persistRouteMapPictureInPictureRestore, routeMapPictureInPictureTitle, setIsMapFloating, showToast]);

  React.useEffect(() => {
    if (!isRouteViewActive || isRouteMapPictureInPictureLinked || typeof window === "undefined") {
      return undefined;
    }

    const restoreState = readRouteMapPictureInPictureRestoreState();
    if (restoreState?.scope !== "route") {
      return undefined;
    }

    if (!hasShownPictureInPictureRestorePromptRef.current) {
      hasShownPictureInPictureRestorePromptRef.current = true;
      showToast({
        type: "info",
        title: "Carte détachée prête",
        message: "Après l'actualisation, clique dans Dispatch pour rouvrir la fenêtre détachée.",
        duration: 7000,
      });
    }

    let restoreRequested = false;
    const handleRestoreActivation = (event) => {
      const isHardRefreshKey = event.type === "keydown" && (
        event.key === "F5"
          || ((event.ctrlKey || event.metaKey) && String(event.key || "").toLowerCase() === "r")
      );

      if (restoreRequested || isHardRefreshKey) {
        return;
      }

      restoreRequested = true;
      window.removeEventListener("pointerdown", handleRestoreActivation, true);
      window.removeEventListener("keydown", handleRestoreActivation, true);
      openFloatingMapOnSecondScreen({ restore: true });
    };

    window.addEventListener("pointerdown", handleRestoreActivation, true);
    window.addEventListener("keydown", handleRestoreActivation, true);
    return () => {
      window.removeEventListener("pointerdown", handleRestoreActivation, true);
      window.removeEventListener("keydown", handleRestoreActivation, true);
    };
  }, [isRouteMapPictureInPictureLinked, isRouteViewActive, openFloatingMapOnSecondScreen, showToast]);

  const routeMapContent = (compactMode, floatingSizeValue = null, { renderLegend = true, renderObservability = true } = {}) => (
    <RouteMapPanel
      assignment={assignment}
      compact={compactMode}
      key={`route-map-${isMapFloating ? "floating" : "inline"}`}
      customMapHeight={isMapFloating ? floatingMapDimensions.mapHeight : null}
      fillAvailableHeight={false}
      floatingSize={floatingSizeValue}
      isFloating={isMapFloating}
      isRouteViewActive={isRouteViewActive}
      observabilityItems={observabilityItems}
      renderGroupRangeToggle={isMapFloating}
      renderInline
      renderLegend={renderLegend}
      renderObservability={renderObservability}
      onClientSelect={onClientSelect}
      onControllerReady={onControllerReady}
      onMapRenderMetaChange={onMapRenderMetaChange}
      onRouteHighlightChange={onRouteHighlightChange}
      selectedClientId={selectedClientId}
      selectionRequest={selectionRequest}
      stateRef={stateRef}
      trip={trip}
    />
  );

  const inlineContent = !isMapFloating && !isRouteMapPictureInPictureLinked ? (
    <section ref={floatingMapAnchorRef} className="grid min-w-0 gap-2">
      {showInlineHeader ? (
        <div className="grid min-w-0 gap-1">
          <h3 className="m-0 text-[1rem] font-semibold leading-tight tracking-[-0.02em] text-text">Carte du trajet</h3>
          <p className="m-0 text-[0.76rem] leading-5 text-content-muted">{routeMapSubtitle}</p>
        </div>
      ) : null}

      {routeMapContent(false)}
    </section>
  ) : null;

  const floatingContent = isMapFloating ? (
    <FloatingRouteMapSurface
      ariaLabel="Carte flottante de la vue route"
      closeLabel="Réduire"
      isDragging={isFloatingMapDragging}
      isResizing={isFloatingMapResizing}
      onClose={() => {
        setIsMapFloating(false);
      }}
      onDragHandlePointerDown={handleFloatingMapPointerDown}
      onKeyDown={handleFloatingMapKeyDown}
      onPointerCancel={handleFloatingMapPointerEnd}
      onPointerMove={handleFloatingMapPointerMove}
      onPointerUp={handleFloatingMapPointerEnd}
      onResizeHandlePointerDown={handleFloatingMapResizePointerDown}
      onSecondaryAction={openFloatingMapOnSecondScreen}
      onSizeChange={handleFloatingMapSizeChange}
      panelRef={floatingMapPanelRef}
      secondaryActionLabel="Détacher"
      size={floatingMapSize}
      style={floatingMapPanelStyle}
      subtitle={routeMapSubtitle}
      title={routeMapTitle}
    >
      {routeMapContent(
        floatingMapSize === "compact",
        floatingMapSize === "compact" ? null : floatingMapSize,
        { renderLegend: false, renderObservability: false },
      )}
    </FloatingRouteMapSurface>
  ) : null;
  const pictureInPictureRouteMapContent = isRouteMapPictureInPictureLinked ? (
    <RouteMapPictureInPicturePanel
      assignment={assignment}
      isRouteViewActive={isRouteViewActive}
      mountNode={routeMapPictureInPictureMountNode}
      onClose={closeRouteMapPictureInPicture}
      onClientSelect={onClientSelect}
      onControllerReady={onControllerReady}
      onMapRenderMetaChange={onMapRenderMetaChange}
      onRouteHighlightChange={onRouteHighlightChange}
      onWindowExpandedChange={handleRouteMapPictureInPictureExpandedChange}
      renderLegend
      selectedClientId={selectedClientId}
      selectionRequest={selectionRequest}
      stateRef={stateRef}
      title={routeMapPictureInPictureTitle}
      trip={trip}
      windowExpanded={isRouteMapPictureInPictureExpanded}
    />
  ) : null;

  return (
    <>
      {inlineContent && hasInlineMountNode ? createPortal(inlineContent, mountNode) : null}
      {floatingContent && floatingMapMountNode instanceof HTMLElement
        ? createPortal(floatingContent, floatingMapMountNode)
        : null}
      {pictureInPictureRouteMapContent}
    </>
  );
}
